/**
 * PresetTemplatePicker.tsx
 * A dialog that lets the user pick one of the 7 quick-pick preset templates,
 * each shown with a live thumbnail preview (rendered from the generated DOCX
 * via docx-preview + modern-screenshot).
 *
 * Performance: thumbnails are captured ONCE and cached in a module-level map,
 * so reopening the dialog is instant. The grid renders immediately (icon
 * placeholder while a thumbnail is still generating), and captures run with
 * bounded concurrency so the main thread stays responsive.
 */
import * as React from "react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "@/components/ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  FileText,
  Loader2,
  Check,
  Search,
  Download,
  Eye,
  Plus,
  Pencil,
  Trash2,
  X,
  Truck,
  FileSpreadsheet,
  Receipt,
  PackageOpen,
  Wallet,
  PackagePlus,
  Undo2,
  UploadCloud,
  FolderOpen,
  LayoutGrid,
  Layers,
} from "lucide-react";
import { renderDocxPreview } from "@/services/docxService";
import { buildPresetDocx, PRESET_TEMPLATES, type PresetTemplateConfig } from "@/utils/presetTemplates";
import { downloadDocxFile } from "@/utils/defaultDocxTemplate";
import { useI18n } from "@/i18n";
import {
  listUserTemplates,
  listUserTags,
  patchUserTemplate,
  deleteUserTemplate,
  addUserTag,
  deleteUserTag,
  saveUserTemplate,
  type UserTemplate,
  type UserTag,
} from "@/utils/userTemplateStore";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (preset: PresetTemplateConfig) => void;
  /** Select a user-saved template (raw docx bytes). */
  onSelectUserTemplate?: (tpl: UserTemplate) => void;
  /** Names of templates that have unsaved editor edits (for a "đã chỉnh" badge). */
  editedTemplateNames?: string[];
  /** Called when a user template is renamed (so the editor can sync fileName). */
  onRenameTemplate?: (oldName: string, newName: string) => void;
  busy?: boolean;
}

/** Map a preset id to a representative icon. */
function presetIcon(id: string) {
  switch (id) {
    case "phieu-giao-hang": return Truck;
    case "bao-gia": return FileSpreadsheet;
    case "hoa-don": return Receipt;
    case "phieu-xuat-kho": return PackageOpen;
    case "phieu-luong": return Wallet;
    case "phieu-nhap-kho": return PackagePlus;
    case "phieu-tra-hang": return Undo2;
    default: return FileText;
  }
}

/** Render one preset DOCX into a hidden container and capture a thumbnail. */
async function captureThumbnail(preset: PresetTemplateConfig): Promise<string | null> {
  const host = document.createElement("div");
  host.style.cssText =
    "position:fixed;left:-10000px;top:0;width:794px;background:#fff;z-index:-1;";
  document.body.appendChild(host);
  try {
    const data = buildPresetDocx(preset);
    await renderDocxPreview(data, host);
    const section = host.querySelector("section.docx") as HTMLElement | null;
    if (!section) return null;
    // Card thumbs: sharp enough for retina cards (~0.55× page, high JPEG).
    const { domToCanvas } = await import("modern-screenshot");
    const canvas = await domToCanvas(section, {
      width: section.offsetWidth,
      height: Math.min(section.offsetHeight, section.offsetWidth * 1.45),
      scale: 0.6,
      backgroundColor: "#ffffff",
    });
    return canvas.toDataURL("image/jpeg", 0.88);
  } catch {
    return null;
  } finally {
    if (host.parentNode) host.parentNode.removeChild(host);
  }
}

// Module-level thumbnail cache: generate once, reuse across dialog opens.
// Bump suffix when capture quality changes so stale low-res blobs are ignored.
const CACHE_VER = "q2";
const thumbCache = new Map<string, string | null>();
const thumbInflight = new Map<string, Promise<string | null>>();

// Module-level LARGE preview cache (crisp popup).
const largeCache = new Map<string, string | null>();
const largeInflight = new Map<string, Promise<string | null>>();

// Module-level cache for user-template thumbnails (keyed by template id).
const userThumbCache = new Map<string, string | null>();
const userThumbInflight = new Map<string, Promise<string | null>>();

function cacheKey(id: string) {
  return `${id}::${CACHE_VER}`;
}

/** Shared offscreen capture for high-quality popup previews. */
async function captureDomPreview(
  data: Uint8Array,
  opts: { scale: number; jpegQuality: number; maxHeightRatio?: number }
): Promise<string | null> {
  const host = document.createElement("div");
  host.style.cssText =
    "position:fixed;left:-10000px;top:0;width:794px;background:#fff;z-index:-1;";
  document.body.appendChild(host);
  try {
    await renderDocxPreview(data, host);
    const section = host.querySelector("section.docx") as HTMLElement | null;
    if (!section) return null;
    const { domToCanvas } = await import("modern-screenshot");
    const maxH = opts.maxHeightRatio
      ? Math.min(section.offsetHeight, section.offsetWidth * opts.maxHeightRatio)
      : section.offsetHeight;
    const canvas = await domToCanvas(section, {
      width: section.offsetWidth,
      height: maxH,
      scale: opts.scale,
      backgroundColor: "#ffffff",
    });
    // PNG for large previews avoids JPEG blockiness on text.
    if (opts.jpegQuality >= 0.95) {
      return canvas.toDataURL("image/png");
    }
    return canvas.toDataURL("image/jpeg", opts.jpegQuality);
  } catch {
    return null;
  } finally {
    if (host.parentNode) host.parentNode.removeChild(host);
  }
}

/** Render a user template's raw bytes and capture a thumbnail. */
async function captureUserThumbnail(tpl: UserTemplate): Promise<string | null> {
  return captureDomPreview(tpl.data, {
    scale: 0.6,
    jpegQuality: 0.88,
    maxHeightRatio: 1.45,
  });
}

/** Render one preset DOCX at high scale for the large preview dialog. */
async function captureLargePreview(preset: PresetTemplateConfig): Promise<string | null> {
  const data = buildPresetDocx(preset);
  return captureDomPreview(data, { scale: 2, jpegQuality: 0.96 });
}

/** High-quality popup preview for a user template (never reuse card thumb). */
async function captureUserLargePreview(tpl: UserTemplate): Promise<string | null> {
  return captureDomPreview(tpl.data, { scale: 2, jpegQuality: 0.96 });
}

/** Run `workers` async jobs concurrently (bounded) to avoid starving the UI. */
async function mapConcurrent<T>(
  items: T[],
  workers: number,
  fn: (item: T) => Promise<string | null>
): Promise<void> {
  let i = 0;
  const run = async () => {
    while (i < items.length) {
      const idx = i++;
      await fn(items[idx]);
    }
  };
  const pool = Array.from({ length: Math.min(workers, items.length) }, () => run());
  await Promise.all(pool);
}

/** Shared card action row — identical on System + My cards. */
function CardActionRow({
  busy,
  onSelect,
  onPreview,
  onDownload,
  downloadName,
}: {
  busy?: boolean;
  onSelect: () => void;
  onPreview: () => void;
  onDownload: (e: React.MouseEvent) => void;
  downloadName: string;
}) {
  const { t } = useI18n();
  return (
    <div className="mt-auto flex items-center gap-1">
      <Button type="button" size="sm" className="h-7 flex-1 text-[11px]" disabled={busy} onClick={onSelect}>
        <Check className="h-3 w-3" /> {t("picker.useThis")}
      </Button>
      <Button
        type="button"
        size="icon"
        variant="outline"
        className="h-7 w-7 shrink-0"
        disabled={busy}
        onClick={onPreview}
        title={t("picker.preview")}
        aria-label={t("picker.preview")}
      >
        <Eye className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-7 w-7 shrink-0"
        disabled={busy}
        onClick={onDownload}
        title={t("picker.downloadFile", { name: downloadName })}
        aria-label={t("picker.downloadFile", { name: downloadName })}
      >
        <Download className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

interface UserTemplateCardProps {
  tpl: UserTemplate;
  thumb: string | null;
  /** Show rename/delete/tag management controls (My Template tab). */
  manage?: boolean;
  /** Show a "đã chỉnh" badge when the template has saved editor edits. */
  edited?: boolean;
  editing: boolean;
  nameDraft: string;
  onNameDraft: (v: string) => void;
  onStartRename: () => void;
  onCommitRename: () => void;
  onCancelRename: () => void;
  onDelete: () => void;
  onSelect: () => void;
  onPreview: () => void;
  onDownload: (e: React.MouseEvent) => void;
  tags: UserTag[];
  onToggleTag: (tag: string) => void;
  /** Create a new tag (if needed) and assign it to this template. */
  onAddTag: (tag: string) => void | Promise<void>;
  busy?: boolean;
}

/** A single user-template card (thumbnail + name + optional manage controls). */
function UserTemplateCard({
  tpl,
  thumb,
  manage,
  edited,
  editing,
  nameDraft,
  onNameDraft,
  onStartRename,
  onCommitRename,
  onCancelRename,
  onDelete,
  onSelect,
  onPreview,
  onDownload,
  tags,
  onToggleTag,
  onAddTag,
  busy,
}: UserTemplateCardProps) {
  const { t } = useI18n();
  const [addingTag, setAddingTag] = React.useState(false);
  const [newTag, setNewTag] = React.useState("");

  const submitCardTag = async () => {
    const name = newTag.trim();
    if (!name) return;
    await onAddTag(name);
    setNewTag("");
    setAddingTag(false);
  };

  return (
    <div className="group flex flex-col overflow-hidden rounded-xl border bg-card text-left shadow-sm transition-all hover:border-primary/40 hover:shadow-md">
      <button
        type="button"
        disabled={busy}
        onClick={onPreview}
        className="relative aspect-[3/4] w-full overflow-hidden bg-muted/40 text-left disabled:cursor-not-allowed"
        title={t("picker.previewBig", { name: tpl.name })}
      >
        {thumb ? (
          <img
            src={thumb}
            alt={tpl.name}
            className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground">
            <FileText className="h-8 w-8 opacity-40" />
            <Loader2 className="h-4 w-4 animate-spin opacity-60" />
          </div>
        )}
        <span className="absolute left-1.5 top-1.5 rounded-md bg-background/90 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground shadow-sm ring-1 ring-border">
          {t("picker.badgeMine")}
        </span>
        {edited && (
          <span className="absolute right-1.5 top-1.5 inline-flex items-center gap-1 rounded-md bg-amber-500 px-1.5 py-0.5 text-[9px] font-semibold text-white shadow">
            <Pencil className="h-2.5 w-2.5" /> {t("picker.editedBadge")}
          </span>
        )}
      </button>
      <div className="flex flex-1 flex-col gap-2 border-t bg-card px-2.5 py-2.5">
        {editing ? (
          <div className="flex items-center gap-1">
            <Input
              value={nameDraft}
              onChange={(e) => onNameDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onCommitRename(); if (e.key === "Escape") onCancelRename(); }}
              className="h-7 px-1.5 text-[11px]"
              autoFocus
            />
            <button type="button" onClick={onCommitRename} className="rounded p-1 text-primary hover:bg-muted" aria-label={t("picker.rename")}>
              <Check className="h-3.5 w-3.5" />
            </button>
            <button type="button" onClick={onCancelRename} className="rounded p-1 text-muted-foreground hover:bg-muted">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex items-start gap-1">
            <p className="min-w-0 flex-1 truncate text-xs font-semibold leading-snug text-foreground" title={tpl.name}>{tpl.name}</p>
            {manage && (
              <div className="flex shrink-0 items-center gap-0.5 opacity-70 transition-opacity group-hover:opacity-100">
                <button
                  type="button"
                  onClick={onStartRename}
                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-primary"
                  title={t("picker.rename")}
                  aria-label={t("picker.renameTemplate", { name: tpl.name })}
                >
                  <Pencil className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={onDelete}
                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                  title={t("picker.deleteTemplate")}
                  aria-label={t("picker.deleteTemplateName", { name: tpl.name })}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        )}
        {manage && (
          <div className="flex flex-wrap items-center gap-1">
            {tags.map((tag) => (
              <button
                key={tag.name}
                type="button"
                onClick={() => onToggleTag(tag.name)}
                className={cn(
                  "rounded-full border px-1.5 py-px text-[9px] transition-colors",
                  tpl.tags.includes(tag.name)
                    ? "border-primary/30 bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:bg-muted/60"
                )}
                title={tpl.tags.includes(tag.name) ? t("picker.untag", { name: tag.name }) : t("picker.tag", { name: tag.name })}
                aria-label={tpl.tags.includes(tag.name) ? t("picker.untagFrom", { name: tag.name, tpl: tpl.name }) : t("picker.tagTo", { name: tag.name, tpl: tpl.name })}
              >
                {tag.name}
              </button>
            ))}
            {addingTag ? (
              <span className="inline-flex items-center gap-0.5">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void submitCardTag();
                    if (e.key === "Escape") { setAddingTag(false); setNewTag(""); }
                  }}
                  placeholder={t("picker.newTagPlaceholder")}
                  className="h-5 w-20 px-1 text-[9px]"
                  autoFocus
                />
                <button type="button" onClick={() => void submitCardTag()} className="rounded p-0.5 text-primary hover:bg-muted" title={t("picker.addTagTitle")}>
                  <Check className="h-3 w-3" />
                </button>
                <button type="button" onClick={() => { setAddingTag(false); setNewTag(""); }} className="rounded p-0.5 text-muted-foreground hover:bg-muted">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setAddingTag(true)}
                className="inline-flex items-center gap-0.5 rounded-full border border-dashed px-1.5 py-px text-[9px] text-muted-foreground hover:border-primary hover:text-primary"
                title={t("picker.addTagTitle")}
              >
                <Plus className="h-2.5 w-2.5" /> {t("picker.addTag")}
              </button>
            )}
          </div>
        )}
        <CardActionRow
          busy={busy}
          onSelect={onSelect}
          onPreview={onPreview}
          onDownload={onDownload}
          downloadName={`${tpl.name}.docx`}
        />
      </div>
    </div>
  );
}

interface SystemTemplateCardProps {
  preset: PresetTemplateConfig;
  thumb: string | null;
  isSelected: boolean;
  busy?: boolean;
  onSelect: () => void;
  onPreview: () => void;
  onDownload: (e: React.MouseEvent) => void;
}

/** System preset card — same action row as user cards. */
function SystemTemplateCard({
  preset,
  thumb,
  isSelected,
  busy,
  onSelect,
  onPreview,
  onDownload,
}: SystemTemplateCardProps) {
  const { t } = useI18n();
  const Icon = presetIcon(preset.id);
  return (
    <div
      className={cn(
        "group flex flex-col overflow-hidden rounded-xl border bg-card text-left shadow-sm transition-all",
        isSelected
          ? "border-primary shadow-md ring-2 ring-primary/25"
          : "hover:border-primary/40 hover:shadow-md",
        busy && "opacity-50"
      )}
    >
      <button
        type="button"
        disabled={busy}
        onClick={onPreview}
        className="relative aspect-[3/4] w-full overflow-hidden bg-muted/40 text-left disabled:cursor-not-allowed"
        title={t("picker.previewBig", { name: preset.name })}
      >
        {thumb ? (
          <img
            src={thumb}
            alt={preset.name}
            className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin opacity-60" />
          </div>
        )}
        <span className="absolute left-1.5 top-1.5 rounded-md bg-background/90 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground shadow-sm ring-1 ring-border">
          {t("picker.badgeSystem")}
        </span>
        {isSelected && (
          <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
            <Check className="h-3 w-3" />
          </span>
        )}
      </button>
      <div className="flex flex-1 flex-col gap-2 border-t px-2.5 py-2.5">
        <div className="flex items-start gap-1.5">
          <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold text-foreground" title={preset.name}>{preset.name}</p>
            <p className="mt-0.5 line-clamp-2 text-[10px] leading-snug text-muted-foreground">{preset.description}</p>
          </div>
        </div>
        <CardActionRow
          busy={busy}
          onSelect={onSelect}
          onPreview={onPreview}
          onDownload={onDownload}
          downloadName={preset.fileName}
        />
      </div>
    </div>
  );
}

export function PresetTemplatePicker({ open, onOpenChange, onSelect, onSelectUserTemplate, editedTemplateNames = [], onRenameTemplate, busy }: Props) {
  const { t } = useI18n();
  const [thumbs, setThumbs] = React.useState<Record<string, string | null>>({});
  const [search, setSearch] = React.useState("");
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  type PreviewTarget =
    | { kind: "system"; id: string }
    | { kind: "user"; id: string }
    | null;
  const [previewTarget, setPreviewTarget] = React.useState<PreviewTarget>(null);
  const [largePreview, setLargePreview] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState<"all" | "system" | "my">("all");
  const [userTemplates, setUserTemplates] = React.useState<UserTemplate[]>([]);
  const [userTags, setUserTags] = React.useState<UserTag[]>([]);
  const [userThumbs, setUserThumbs] = React.useState<Record<string, string | null>>({});
  const [activeTag, setActiveTag] = React.useState<string | null>(null);
  const [editingName, setEditingName] = React.useState<string | null>(null); // template id
  const [nameDraft, setNameDraft] = React.useState("");
  const [addingTag, setAddingTag] = React.useState(false);
  const [newTagName, setNewTagName] = React.useState("");
  const [dragging, setDragging] = React.useState(false);
  const uploadInputRef = React.useRef<HTMLInputElement>(null);

  // Load user templates + tags whenever the dialog opens.
  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [tpls, tags] = await Promise.all([listUserTemplates(), listUserTags()]);
        if (!cancelled) {
          setUserTemplates(tpls);
          setUserTags(tags);
        }
      } catch {
        /* IndexedDB unavailable — stay empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Generate thumbnails for user templates (cached, bounded concurrency).
  React.useEffect(() => {
    if (!open || userTemplates.length === 0) return;
    let cancelled = false;
    const pending = userTemplates.filter((t) => !userThumbCache.has(cacheKey(t.id)));
    if (pending.length === 0) {
      const snapshot: Record<string, string | null> = {};
      for (const t of userTemplates) {
        const v = userThumbCache.get(cacheKey(t.id));
        if (v !== undefined) snapshot[t.id] = v;
      }
      if (Object.keys(snapshot).length) setUserThumbs((prev) => ({ ...prev, ...snapshot }));
      return;
    }
    void mapConcurrent(pending, 3, (t) => {
      const key = cacheKey(t.id);
      if (userThumbInflight.has(key)) return userThumbInflight.get(key)!;
      const job = captureUserThumbnail(t).then((v) => {
        userThumbCache.set(key, v);
        userThumbInflight.delete(key);
        if (!cancelled) setUserThumbs((prev) => ({ ...prev, [t.id]: v }));
        return v;
      });
      userThumbInflight.set(key, job);
      return job;
    });
    return () => {
      cancelled = true;
    };
  }, [open, userTemplates]);

  // Kick off thumbnail generation once (cached) when the dialog first opens.
  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const sync = () => {
      if (cancelled) return;
      // Pull whatever is cached/in-flight into state immediately.
      const snapshot: Record<string, string | null> = {};
      for (const p of PRESET_TEMPLATES) {
        const v = thumbCache.get(cacheKey(p.id));
        if (v !== undefined) snapshot[p.id] = v;
      }
      if (Object.keys(snapshot).length > 0) setThumbs((prev) => ({ ...prev, ...snapshot }));
    };

    // Generate any missing thumbnails (bounded concurrency), then sync.
    const pending = PRESET_TEMPLATES.filter((p) => !thumbCache.has(cacheKey(p.id)));
    if (pending.length === 0) {
      sync();
      return;
    }

    void mapConcurrent(pending, 3, (p) => {
      const key = cacheKey(p.id);
      if (thumbInflight.has(key)) return thumbInflight.get(key)!;
      const job = captureThumbnail(p).then((v) => {
        thumbCache.set(key, v);
        thumbInflight.delete(key);
        sync();
        return v;
      });
      thumbInflight.set(key, job);
      return job;
    });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return PRESET_TEMPLATES;
    return PRESET_TEMPLATES.filter(
      (p) => p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q)
    );
  }, [search]);

  const handleDownload = (e: React.MouseEvent, preset: PresetTemplateConfig) => {
    e.stopPropagation();
    downloadDocxFile(buildPresetDocx(preset), preset.fileName);
  };

  /** Commit a rename edit for a user template. */
  const commitRename = async (id: string) => {
    const name = nameDraft.trim();
    if (name) {
      const old = userTemplates.find((t) => t.id === id)?.name;
      await patchUserTemplate(id, { name });
      setUserTemplates((prev) =>
        prev.map((t) => (t.id === id ? { ...t, name } : t))
      );
      if (old && old !== name) onRenameTemplate?.(old, name);
    }
    setEditingName(null);
    setNameDraft("");
  };

  /** Delete a user template (and its card). */
  const removeUserTemplate = async (id: string) => {
    await deleteUserTemplate(id);
    setUserTemplates((prev) => prev.filter((t) => t.id !== id));
  };

  /** Add a new user tag to the registry. */
  const submitNewTag = async () => {
    const name = newTagName.trim();
    if (!name) return;
    await addUserTag(name);
    setUserTags(await listUserTags());
    setNewTagName("");
    setAddingTag(false);
  };

  /** Delete a tag from the registry + all templates using it. */
  const removeTag = async (name: string) => {
    await deleteUserTag(name);
    setUserTags(await listUserTags());
    setUserTemplates((prev) =>
      prev.map((t) => (t.tags.includes(name) ? { ...t, tags: t.tags.filter((x) => x !== name) } : t))
    );
    if (activeTag === name) setActiveTag(null);
  };

  /** Toggle a tag on a user template. */
  const toggleTemplateTag = async (tplId: string, tag: string) => {
    const tpl = userTemplates.find((t) => t.id === tplId);
    if (!tpl) return;
    const tags = tpl.tags.includes(tag)
      ? tpl.tags.filter((t) => t !== tag)
      : [...tpl.tags, tag];
    await patchUserTemplate(tplId, { tags });
    setUserTemplates((prev) => prev.map((t) => (t.id === tplId ? { ...t, tags } : t)));
  };

  /** Which templates to show given the active tab + tag filter. */
  const visibleUserTemplates = React.useMemo(() => {
    if (activeTag) return userTemplates.filter((t) => t.tags.includes(activeTag));
    return userTemplates;
  }, [userTemplates, activeTag]);

  // Generate the crisp large preview when the preview dialog opens (system or user).
  React.useEffect(() => {
    if (!previewTarget) {
      setLargePreview(null);
      return;
    }
    let cancelled = false;
    if (previewTarget.kind === "system") {
      const preset = PRESET_TEMPLATES.find((p) => p.id === previewTarget.id);
      if (!preset) return;
      const key = cacheKey(preset.id);
      if (largeCache.has(key)) {
        setLargePreview(largeCache.get(key)!);
        return;
      }
      if (largeInflight.has(key)) {
        largeInflight.get(key)!.then((v) => { if (!cancelled) setLargePreview(v); });
        return () => { cancelled = true; };
      }
      const job = captureLargePreview(preset).then((v) => {
        largeCache.set(key, v);
        largeInflight.delete(key);
        if (!cancelled) setLargePreview(v);
        return v;
      });
      largeInflight.set(key, job);
      return () => { cancelled = true; };
    }
    // User template: always capture high-res (never reuse card thumb).
    const tpl = userTemplates.find((t) => t.id === previewTarget.id);
    if (!tpl) return;
    const key = cacheKey(`user:${tpl.id}`);
    if (largeCache.has(key)) {
      setLargePreview(largeCache.get(key)!);
      return;
    }
    if (largeInflight.has(key)) {
      largeInflight.get(key)!.then((v) => { if (!cancelled) setLargePreview(v); });
      return () => { cancelled = true; };
    }
    setLargePreview(null);
    const job = captureUserLargePreview(tpl).then((v) => {
      largeCache.set(key, v);
      largeInflight.delete(key);
      if (!cancelled) setLargePreview(v);
      return v;
    });
    largeInflight.set(key, job);
    return () => {
      cancelled = true;
    };
  }, [previewTarget, userTemplates]);

  /** Create tag (registry) + assign to a template. */
  const addTagToTemplate = async (tplId: string, raw: string) => {
    const name = raw.trim();
    if (!name) return;
    await addUserTag(name);
    setUserTags(await listUserTags());
    const tpl = userTemplates.find((t) => t.id === tplId);
    if (!tpl) return;
    if (tpl.tags.includes(name)) return;
    const tags = [...tpl.tags, name];
    await patchUserTemplate(tplId, { tags });
    setUserTemplates((prev) => prev.map((t) => (t.id === tplId ? { ...t, tags } : t)));
  };

  const gridCols = "grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4";

  const userCardProps = (tpl: UserTemplate, manage?: boolean) => ({
    tpl,
    thumb: userThumbs[tpl.id] ?? null,
    manage,
    edited: editedTemplateNames.includes(tpl.name),
    editing: editingName === tpl.id,
    nameDraft,
    onNameDraft: setNameDraft,
    onStartRename: () => { setNameDraft(tpl.name); setEditingName(tpl.id); },
    onCommitRename: () => commitRename(tpl.id),
    onCancelRename: () => setEditingName(null),
    onDelete: () => removeUserTemplate(tpl.id),
    onSelect: () => onSelectUserTemplate?.(tpl),
    onPreview: () => setPreviewTarget({ kind: "user", id: tpl.id }),
    onDownload: (e: React.MouseEvent) => {
      e.stopPropagation();
      const fileName = tpl.name.toLowerCase().endsWith(".docx") ? tpl.name : `${tpl.name}.docx`;
      downloadDocxFile(tpl.data, fileName);
    },
    tags: userTags,
    onToggleTag: (tag: string) => toggleTemplateTag(tpl.id, tag),
    onAddTag: (tag: string) => addTagToTemplate(tpl.id, tag),
    busy,
  });

  const handleUploadFiles = async (files: FileList | File[]) => {
    // Library-only: catalog into Mine and stay open (does not swap editor template).
    if (busy) return;
    const list = Array.from(files as ArrayLike<File>).filter((f) =>
      f.name.toLowerCase().endsWith(".docx")
    );
    if (list.length === 0) return;
    try {
      for (const file of list) {
        const buffer = new Uint8Array(await file.arrayBuffer());
        const baseName = file.name.replace(/\.docx$/i, "") || file.name;
        const tpl: UserTemplate = {
          id: crypto.randomUUID(),
          name: baseName,
          data: buffer,
          tags: [],
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };
        await saveUserTemplate(tpl);
      }
      setUserTemplates(await listUserTemplates());
      setActiveTab("my");
      setActiveTag(null);
    } catch {
      /* IndexedDB unavailable */
    }
  };

  /** Full-library drag state (enter/leave depth avoids flicker on child nodes). */
  const dragDepthRef = React.useRef(0);
  const isFileDrag = (dt: DataTransfer | null) => {
    if (!dt) return false;
    const types = Array.from(dt.types ?? []);
    // Empty types still happens on some synthetic / OS drags — allow and filter on drop.
    if (types.length === 0) return true;
    return types.includes("Files") || types.includes("application/x-moz-file");
  };
  const onLibraryDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    if (!isFileDrag(e.dataTransfer)) return;
    dragDepthRef.current += 1;
    setDragging(true);
  };
  const onLibraryDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    if (!isFileDrag(e.dataTransfer)) return;
    e.dataTransfer.dropEffect = "copy";
    if (!dragging) setDragging(true);
  };
  const onLibraryDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragging(false);
  };
  const onLibraryDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setDragging(false);
    if (busy) return;
    const files = e.dataTransfer.files;
    if (files?.length) void handleUploadFiles(files);
  };

  // Reset drag chrome when drawer closes
  React.useEffect(() => {
    if (!open) {
      dragDepthRef.current = 0;
      setDragging(false);
    }
  }, [open]);

  const renderSystemGrid = () => (
    filtered.length === 0 ? (
      <div className="rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">
        {t("picker.noMatch", { query: search })}
      </div>
    ) : (
      <div className={gridCols}>
        {filtered.map((preset) => (
          <SystemTemplateCard
            key={preset.id}
            preset={preset}
            thumb={thumbs[preset.id] ?? null}
            isSelected={selectedId === preset.id}
            busy={busy}
            onSelect={() => {
              setSelectedId(preset.id);
              onSelect(preset);
            }}
            onPreview={() => setPreviewTarget({ kind: "system", id: preset.id })}
            onDownload={(e) => handleDownload(e, preset)}
          />
        ))}
      </div>
    )
  );

  const previewSystem = previewTarget?.kind === "system"
    ? PRESET_TEMPLATES.find((p) => p.id === previewTarget.id) ?? null
    : null;
  const previewUser = previewTarget?.kind === "user"
    ? userTemplates.find((t) => t.id === previewTarget.id) ?? null
    : null;
  const previewOpen = previewTarget !== null;
  const previewTitle = previewSystem?.name ?? previewUser?.name ?? "";
  const previewDesc = previewSystem?.description ?? "";

  return (
    <>
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-w-[75vw]">
        <div
          data-library-drop-root
          title={t("picker.dropTitle")}
          onDragEnter={onLibraryDragEnter}
          onDragOver={onLibraryDragOver}
          onDragLeave={onLibraryDragLeave}
          onDrop={onLibraryDrop}
          className={cn(
            "relative flex min-h-0 flex-1 flex-col",
            dragging && "ring-2 ring-inset ring-primary/40"
          )}
        >
          <input
            ref={uploadInputRef}
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = e.target.files;
              if (files?.length) void handleUploadFiles(files);
              e.target.value = "";
            }}
          />

          {/* Full-surface drop overlay */}
          {dragging && (
            <div
              className="pointer-events-none absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 bg-primary/10 backdrop-blur-[1px]"
              aria-hidden
            >
              <div className="flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-primary bg-background/95 px-8 py-6 shadow-lg">
                <UploadCloud className="h-8 w-8 text-primary" />
                <p className="text-sm font-semibold text-primary">{t("picker.dropOverlay")}</p>
                <p className="text-xs text-muted-foreground">{t("picker.dropOverlayHint")}</p>
              </div>
            </div>
          )}

          <DrawerHeader className="shrink-0 space-y-1 border-b pr-12">
            <DrawerTitle className="text-base">{t("picker.title")}</DrawerTitle>
            <DrawerDescription className="text-xs">
              {t("picker.desc")}
            </DrawerDescription>
          </DrawerHeader>

          <div className="flex min-h-0 flex-1">
            {/* Left library nav */}
            <aside className="flex w-[11.5rem] shrink-0 flex-col gap-1 border-r bg-muted/20 p-2 sm:w-52">
              <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t("picker.navLibrary")}
              </p>
              <button
                type="button"
                onClick={() => setActiveTab("all")}
                className={cn(
                  "flex items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs transition-colors",
                  activeTab === "all"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-foreground hover:bg-muted"
                )}
              >
                <Layers className="h-3.5 w-3.5 shrink-0 opacity-90" />
                <span className="min-w-0 flex-1 truncate font-medium">{t("picker.tabAll")}</span>
                <span className={cn(
                  "rounded-full px-1.5 py-px text-[10px] tabular-nums",
                  activeTab === "all" ? "bg-primary-foreground/20" : "bg-muted text-muted-foreground"
                )}>
                  {userTemplates.length + PRESET_TEMPLATES.length}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("my")}
                className={cn(
                  "flex items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs transition-colors",
                  activeTab === "my"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-foreground hover:bg-muted"
                )}
              >
                <FolderOpen className="h-3.5 w-3.5 shrink-0 opacity-90" />
                <span className="min-w-0 flex-1 truncate font-medium">{t("picker.tabMy")}</span>
                <span className={cn(
                  "rounded-full px-1.5 py-px text-[10px] tabular-nums",
                  activeTab === "my" ? "bg-primary-foreground/20" : "bg-muted text-muted-foreground"
                )}>
                  {userTemplates.length}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("system")}
                className={cn(
                  "flex items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs transition-colors",
                  activeTab === "system"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-foreground hover:bg-muted"
                )}
              >
                <LayoutGrid className="h-3.5 w-3.5 shrink-0 opacity-90" />
                <span className="min-w-0 flex-1 truncate font-medium">{t("picker.tabSystem")}</span>
                <span className={cn(
                  "rounded-full px-1.5 py-px text-[10px] tabular-nums",
                  activeTab === "system" ? "bg-primary-foreground/20" : "bg-muted text-muted-foreground"
                )}>
                  {PRESET_TEMPLATES.length}
                </span>
              </button>

              <div className="mt-auto space-y-2 border-t pt-2">
                <Button
                  type="button"
                  size="sm"
                  className="h-8 w-full justify-start text-xs"
                  disabled={busy}
                  onClick={() => uploadInputRef.current?.click()}
                  title={t("picker.addToLibraryHint")}
                >
                  <Plus className="h-3.5 w-3.5" /> {t("picker.addToLibrary")}
                </Button>
                <p className="px-1 text-[10px] leading-snug text-muted-foreground">
                  {t("picker.addToLibraryHint")}
                </p>
              </div>
            </aside>

            {/* Main content */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <div className="flex shrink-0 items-center gap-2 border-b px-3 py-2">
                <div className="relative min-w-0 flex-1">
                  <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t("picker.searchPlaceholder")}
                    className="h-9 pl-8 text-xs"
                  />
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
                <div className="space-y-3">
                  {activeTab === "all" && (
                    <div className="space-y-5">
                      {userTemplates.length > 0 && (
                        <section className="space-y-3">
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("picker.sectionMy")}</span>
                            <span className="rounded-full bg-muted px-1.5 py-px text-[10px] font-medium tabular-nums text-muted-foreground">{userTemplates.length}</span>
                            <div className="h-px flex-1 bg-border" />
                          </div>
                          <div className={gridCols}>
                            {userTemplates
                              .filter((tpl) => {
                                const q = search.trim().toLowerCase();
                                if (!q) return true;
                                return tpl.name.toLowerCase().includes(q) || tpl.tags.some((tg) => tg.toLowerCase().includes(q));
                              })
                              .map((tpl) => (
                                <UserTemplateCard key={tpl.id} {...userCardProps(tpl)} />
                              ))}
                          </div>
                        </section>
                      )}
                      <section className="space-y-3">
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("picker.sectionSystem")}</span>
                          <span className="rounded-full bg-muted px-1.5 py-px text-[10px] font-medium tabular-nums text-muted-foreground">{filtered.length}</span>
                          <div className="h-px flex-1 bg-border" />
                        </div>
                        {renderSystemGrid()}
                      </section>
                    </div>
                  )}

                  {activeTab === "my" && (
                    <>
                      <button
                        type="button"
                        onClick={() => uploadInputRef.current?.click()}
                        disabled={busy}
                        className={cn(
                          "flex w-full items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-2.5 text-xs transition-colors",
                          "border-muted-foreground/25 text-muted-foreground hover:border-primary/40 hover:bg-muted/30",
                          busy && "cursor-not-allowed opacity-50"
                        )}
                      >
                        <UploadCloud className="h-4 w-4 shrink-0" />
                        <span dangerouslySetInnerHTML={{ __html: t("picker.dropText") }} />
                      </button>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setActiveTag(null)}
                          className={cn(
                            "rounded-full border px-2.5 py-0.5 text-[11px] transition-colors",
                            !activeTag ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted/60"
                          )}
                        >
                          {t("picker.allCount", { count: userTemplates.length })}
                        </button>
                        {userTags.map((tag) => (
                          <span key={tag.name} className="inline-flex items-center gap-0.5">
                            <button
                              type="button"
                              onClick={() => setActiveTag(activeTag === tag.name ? null : tag.name)}
                              className={cn(
                                "rounded-full border px-2.5 py-0.5 text-[11px] transition-colors",
                                activeTag === tag.name
                                  ? "border-primary bg-primary text-primary-foreground"
                                  : "border-border hover:bg-muted/60"
                              )}
                            >
                              {tag.name}
                            </button>
                            <button
                              type="button"
                              onClick={() => removeTag(tag.name)}
                              className="rounded-full p-0.5 text-muted-foreground hover:text-destructive"
                              title={`Xóa tag ${tag.name}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                        {addingTag ? (
                          <span className="inline-flex items-center gap-1">
                            <Input
                              value={newTagName}
                              onChange={(e) => setNewTagName(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") submitNewTag(); if (e.key === "Escape") setAddingTag(false); }}
                              placeholder={t("picker.newTagPlaceholder")}
                              className="h-6 w-28 px-1.5 text-[11px]"
                              autoFocus
                            />
                            <button type="button" onClick={submitNewTag} className="rounded p-0.5 text-primary hover:bg-muted/60" title={t("picker.addTagTitle")}>
                              <Check className="h-3.5 w-3.5" />
                            </button>
                            <button type="button" onClick={() => setAddingTag(false)} className="rounded p-0.5 text-muted-foreground hover:bg-muted/60">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setAddingTag(true)}
                            className="inline-flex items-center gap-0.5 rounded-full border border-dashed px-2.5 py-0.5 text-[11px] text-muted-foreground hover:border-primary hover:text-primary"
                          >
                            <Plus className="h-3 w-3" /> {t("picker.addTag")}
                          </button>
                        )}
                      </div>
                      <div className={gridCols}>
                        {visibleUserTemplates
                          .filter((tpl) => {
                            const q = search.trim().toLowerCase();
                            if (!q) return true;
                            return tpl.name.toLowerCase().includes(q) || tpl.tags.some((tg) => tg.toLowerCase().includes(q));
                          })
                          .map((tpl) => (
                            <UserTemplateCard key={tpl.id} {...userCardProps(tpl, true)} />
                          ))}
                        {visibleUserTemplates.length === 0 && (
                          <div className="col-span-full flex flex-col items-center gap-3 rounded-xl border border-dashed bg-muted/20 py-14 text-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                              <FolderOpen className="h-6 w-6 text-muted-foreground/60" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground">{t("picker.emptyTitle")}</p>
                              <p className="mx-auto mt-1 max-w-sm text-xs text-muted-foreground">{t("picker.emptyDesc")}</p>
                            </div>
                            <Button type="button" size="sm" className="h-8 text-xs" onClick={() => uploadInputRef.current?.click()}>
                              <Plus className="h-3.5 w-3.5" /> {t("picker.addToLibrary")}
                            </Button>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {activeTab === "system" && renderSystemGrid()}
                </div>
              </div>
            </div>
          </div>

          <DrawerFooter className="shrink-0 flex-row items-center justify-between sm:justify-between">
            <p className="text-[10px] text-muted-foreground">
              {t("picker.footer", {
                count:
                  activeTab === "my"
                    ? userTemplates.length
                    : activeTab === "all"
                      ? userTemplates.length + filtered.length
                      : filtered.length,
              })}
            </p>
            <Button variant="outline" size="sm" className="h-8" onClick={() => onOpenChange(false)}>
              {t("picker.close")}
            </Button>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>

    <Dialog open={previewOpen} onOpenChange={(o) => { if (!o) setPreviewTarget(null); }}>
      <DialogContent className="max-w-5xl gap-3 sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="text-base">{previewTitle}</DialogTitle>
          {previewDesc ? (
            <DialogDescription className="text-xs">{previewDesc}</DialogDescription>
          ) : (
            <DialogDescription className="sr-only">{previewTitle}</DialogDescription>
          )}
        </DialogHeader>
        <div className="max-h-[78vh] overflow-auto rounded-lg border bg-slate-100 p-4">
          {largePreview ? (
            <img
              src={largePreview}
              alt={previewTitle}
              className="mx-auto h-auto w-full max-w-3xl rounded bg-white object-contain shadow"
            />
          ) : (
            <div className="flex h-72 items-center justify-center text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin opacity-60" />
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2">
          {previewSystem && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                downloadDocxFile(buildPresetDocx(previewSystem), previewSystem.fileName);
              }}
            >
              <Download className="h-3.5 w-3.5" /> {t("picker.downloadDocx")}
            </Button>
          )}
          {previewUser && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const fileName = previewUser.name.toLowerCase().endsWith(".docx")
                  ? previewUser.name
                  : `${previewUser.name}.docx`;
                downloadDocxFile(previewUser.data, fileName);
              }}
            >
              <Download className="h-3.5 w-3.5" /> {t("picker.downloadDocx")}
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => {
              if (previewSystem) {
                setSelectedId(previewSystem.id);
                setPreviewTarget(null);
                onSelect(previewSystem);
              } else if (previewUser) {
                setPreviewTarget(null);
                onSelectUserTemplate?.(previewUser);
              }
            }}
          >
            <Check className="h-3.5 w-3.5" /> {t("picker.useThis")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}

